import { useMemo } from "react";
import type { ReactNode } from "react";

import { AccountContext } from "./account.ts";
import type { Account } from "./account.ts";
import { supabase } from "./supabase.ts";
import { useMe } from "./useMe.ts";
import { useSession } from "./useSession.ts";

/**
 * One session and one `/me` for the whole route tree.
 *
 * Context rather than props because route elements are constructed in the route
 * table, with nowhere to pass them from. Fetching once here also means moving
 * between destinations is a re-render and not a refetch of the account — which
 * it would be if each route called `useMe` for itself.
 */
export function AccountProvider({ children }: { children: ReactNode }) {
  const { session, loading } = useSession();
  const { state: me, replace: replaceMe } = useMe(session?.user.id);

  const value = useMemo<Account>(
    () => ({
      session,
      loading,
      me,
      replaceMe,
      signOut: () => void supabase.auth.signOut(),
    }),
    [session, loading, me, replaceMe],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}
